// src/Pages/EditorPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { useLocation, useNavigate } from "react-router-dom";

import Layout from "../components/Layout";
import { useTheme } from "@/theme";

import ScratchPadPanel from "@/components/ScratchPadPanel";
import AIAssistPanel, { AssistantMessage } from "@/components/AIAssistPanel";
import LateNightSnackPanel, {
  SnackSpot as SnackSpotRecord,
  SnackSpotDraft,
} from "@/components/LateNightSnackPanel";
import { formatCitation, buildInlineReferenceVariants, type InlineVariantKey } from "@/lib/paperFormats";
import {
  refineThesisStatement,
  requestAIAssistCommand,
  sendAIAssistChat,
  type AssistantConversationMessage,
  type AIAssistCommandResult,
} from "@/lib/openaiClient";
import DictionaryPanel from "@/components/DictionaryPanel";
import LibraryPanel from "@/components/LibraryPanel";
import CitationAdvisorPanel from "@/components/CitationAdvisorPanel";
import ReferenceListPanel from "@/components/ReferenceListPanel";
import CitationSuggestionPanel from "@/components/CitationSuggestionPanel";
import AIMonitor from "@/components/AIMonitor";
import TopicAssistant from "@/components/TopicAssistant";
import ThesisStatementPanel from "@/components/ThesisStatementPanel";
import IntegrationsPanel from "@/components/IntegrationsPanel";
import CollaborationPanel from "@/components/CollaborationPanel";
import UploadPanel, { DemoUploadRecord } from "@/components/UploadPanel";
import MusicPlayerDock from "@/components/MusicPlayerDock";
import { Switch } from "@/components/ui/switch";
import html2pdf from "html2pdf.js";
import { convertHtmlToFile, isCloudConvertEnabled } from "@/lib/cloudConvertClient";
import "quill/dist/quill.bubble.css";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "@/lib/quillPageBreak";
import { useEntity } from "@/hooks/useEntity";
import { citationEntityConfig } from "@/entities/Citation";
import { documentEntityConfig } from "@/entities/Document";
import { userEntityConfig } from "@/entities/User";
import { scratchPadEntityConfig } from "@/entities/ScratchPad";
import { snackSpotEntityConfig } from "@/entities/SnackSpot";
import { collaborationEntityConfig } from "@/entities/Collaboration";
import { uploadedDocumentEntityConfig } from "@/entities/UploadedDocument";
import { organizerItemEntityConfig } from "@/entities/OrganizerItem";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  ArrowUpRight,
  Bell,
  Book,
  BookOpen,
  Clipboard,
  ExternalLink,
  FileDown,
  FilePlus,
  FilePlus2,
  FileText,
  FolderOpen,
  Headphones,
  LayoutDashboard,
  Lightbulb,
  Library,
  Link2,
  Loader2,
  Mic,
  MicOff,
  Minus,
  Music2,
  Pause,
  PenSquare,
  Play,
  Plus,
  Printer,
  Save,
  Search,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Upload,
  UploadCloud,
  Utensils,
  Users,
  Waves,
} from "lucide-react";

declare global {
  interface Window {
    webkitSpeechRecognition?: any;
    SpeechRecognition?: any;
  }
}

/* ---------------- React‑Quill shim for React 18 ------------------- */
if (!(ReactDOM as any).findDOMNode) {
  (ReactDOM as any).findDOMNode = function (instance: any) {
    if (!instance) return null;
    if ((instance as any).nodeType === 1) return instance;
    try {
      if (typeof (instance as any).getEditor === "function") {
        const editor = (instance as any).getEditor();
        return (editor && (editor as any).root) || null;
      }
    } catch {
      // ignore
    }
    return (instance as any)?.base || (instance as any)?.root || null;
  };
}

/* ---------------- Style templates ------------------- */
type DocumentFormat =
  | "APA"
  | "MLA"
  | "Chicago"
  | "Turabian"
  | "Harvard"
  | "AMA"
  | "IEEE";

const STYLE_TEMPLATES: { [key in DocumentFormat]: string } = {
  APA: `
    <h1 style="text-align:center;">Paper Title</h1>
    <p style="text-align:center;"><em>Author Name</em></p>
    <p style="text-align:center;"><em>Institution</em></p>
    <hr />
    <h2>Abstract</h2>
    <p>Write a brief summary of your paper here (150–250 words).</p>
    <h2>Introduction</h2>
    <p>Begin your introduction here.</p>
    <h2>Method</h2>
    <p>Describe your method here.</p>
    <h2>Results</h2>
    <p>Describe your results here.</p>
    <h2>Discussion</h2>
    <p>Discuss your findings here.</p>
    <h2>References</h2>
    <p>Lastname, A. A. (Year). <em>Title of the work</em>. Publisher.</p>
  `,
  MLA: `
    <p>Student Name</p>
    <p>Professor Name</p>
    <p>Course Name</p>
    <p>Day Month Year</p>
    <h1 style="text-align:center;">Paper Title</h1>
    <p>Start your MLA-style paper here. Indent the first line of each paragraph by 0.5 inches.</p>
    <h2>Works Cited</h2>
    <p>Lastname, Firstname. <em>Title of Book</em>. Publisher, Year.</p>
  `,
  Chicago: `
    <h1 style="text-align:center;">Paper Title</h1>
    <p style="text-align:center;">Student Name</p>
    <p style="text-align:center;">Course • Instructor • Date</p>
    <h2>Introduction</h2>
    <p>Begin your Chicago-style paper here.</p>
    <h2>Bibliography</h2>
    <p>Lastname, Firstname. <em>Title of Book</em>. Place: Publisher, Year.</p>
  `,
  Turabian: `
    <h1 style="text-align:center;">Paper Title</h1>
    <p style="text-align:center;">Student Name</p>
    <p style="text-align:center;">Course</p>
    <p style="text-align:center;">Date</p>
    <h2>Introduction</h2>
    <p>Begin your Turabian-style paper here.</p>
    <h2>Bibliography</h2>
    <p>Lastname, Firstname. <em>Title of Book</em>. City: Publisher, Year.</p>
  `,
  Harvard: `
    <h1 style="text-align:center;">Paper Title</h1>
    <p style="text-align:center;">Student Name</p>
    <p style="text-align:center;">Institution</p>
    <h2>Introduction</h2>
    <p>Begin your Harvard-style paper here.</p>
    <h2>Reference List</h2>
    <p>Lastname, Initial(s). (Year) <em>Title of book</em>. Publisher.</p>
  `,
  AMA: `
    <h1 style="text-align:center;">Paper Title</h1>
    <p style="text-align:center;">Student Name</p>
    <p style="text-align:center;">Institution</p>
    <h2>Introduction</h2>
    <p>Begin your AMA-style paper here.</p>
    <h2>References</h2>
    <ol>
      <li>Lastname Firstname. <em>Title of Book</em>. Publisher; Year.</li>
    </ol>
  `,
  IEEE: `
    <h1 style="text-align:center;">Paper Title</h1>
    <p style="text-align:center;">Author Name</p>
    <p style="text-align:center;">Institution</p>
    <h2>Abstract</h2>
    <p>Write your IEEE abstract here.</p>
    <h2>I. INTRODUCTION</h2>
    <p>Begin your IEEE-style paper here.</p>
    <h2>REFERENCES</h2>
    <ol>
      <li>[1] A. Author, <em>Title of paper</em>, Journal, vol. x, no. y, pp. z–zz, Year.</li>
    </ol>
  `,
};

type TitlePageFields = {
  studentName: string;
  institutionName: string;
  courseName: string;
  instructorName: string;
  dueDate: string;
};

const TITLE_FIELD_LABELS: Record<keyof TitlePageFields, string> = {
  studentName: "Student",
  institutionName: "Institution",
  courseName: "Course",
  instructorName: "Instructor",
  dueDate: "Due Date",
};

const friendlyDueDate = () =>
  new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date());

const createDefaultTitleFields = (): TitlePageFields => ({
  studentName: "Student Name",
  institutionName: "Institution Name",
  courseName: "Course Title / Section",
  instructorName: "Instructor Name",
  dueDate: friendlyDueDate(),
});

const buildTitlePageLines = (
  format: DocumentFormat,
  fields: TitlePageFields,
  documentTitle: string
): string[] => {
  const safeTitle = documentTitle || "Untitled Manuscript";
  switch (format) {
    case "MLA":
      return [
        fields.studentName,
        fields.instructorName,
        fields.courseName,
        fields.dueDate,
        "",
        safeTitle,
      ];
    case "Chicago":
    case "Turabian":
      return [safeTitle, fields.studentName, fields.courseName, fields.instructorName, fields.dueDate];
    case "Harvard":
    case "AMA":
    case "IEEE":
      return [
        safeTitle,
        fields.studentName,
        `${fields.courseName} • ${fields.instructorName}`,
        fields.institutionName,
        fields.dueDate,
      ];
    case "APA":
    default:
      return [
        safeTitle,
        fields.studentName,
        fields.institutionName,
        fields.courseName,
        fields.instructorName,
        fields.dueDate,
      ];
  }
};

const formatTitlePageText = (
  format: DocumentFormat,
  fields: TitlePageFields,
  documentTitle: string
) => buildTitlePageLines(format, fields, documentTitle).join("\n");

const STYLE_GUIDE_LINKS: Record<DocumentFormat, string> = {
  APA: "https://apastyle.apa.org/style-grammar-guidelines/paper-format",
  MLA: "https://style.mla.org/formatting-papers/",
  Chicago: "https://www.chicagomanualofstyle.org/tools_citationguide/citation-guide-1.html",
  Turabian: "https://www.press.uchicago.edu/books/turabian/turabian_citationguide.html",
  Harvard: "https://www.mendeley.com/guides/harvard-citation-guide",
  AMA: "https://www.amamanualofstyle.com/",
  IEEE: "https://ieeeauthorcenter.ieee.org/wp-content/uploads/IEEE-Editorial-Style-Manual.pdf",
};

type HtmlDocx = {
  asBlob: (html: string) => Blob;
};

const loadHtmlDocx = async (): Promise<HtmlDocx> => {
  if (typeof window === "undefined") {
    throw new Error("DOCX export is only available in the browser.");
  }
  const cached = (window as any).htmlDocx;
  if (cached) {
    return cached as HtmlDocx;
  }
  await import(
    /* @vite-ignore */ "https://cdn.jsdelivr.net/npm/html-docx-js@0.3.1/dist/html-docx.min.js"
  );
  const loaded = (window as any).htmlDocx;
  if (!loaded) {
    throw new Error("Failed to load html-docx-js");
  }
  return loaded as HtmlDocx;
};

const sanitizeFileName = (raw?: string) => {
  const base = raw?.trim() || "thesiscribe-draft";
  return base.replace(/[^a-z0-9-_]+/gi, "_");
};

/* ---------------- Entity types used by useEntity ------------------- */
type Citation = {
  id: number;
  documentId: number;
  citationType: string;
  authors: string;
  title: string;
  year: string;
  publisher: string;
  url: string;
  doi: string;
  pages: string;
  volume: string;
  issue: string;
  inTextLocations: string;
  formattedReference: string;
  created_at: string;
  updated_at: string;
};

type ReferenceLike = Pick<Citation, "id" | "documentId" | "citationType" | "authors" | "title" | "year"> &
  Partial<Citation>;

type ThesisRefinementResult = {
  improvedText: string;
  rationale: string;
  talkingPoints: string[];
  hook?: string;
};

type Document = {
  id: number;
  title: string;
  content: string;
  paperFormat: DocumentFormat;
  userId?: number;
  topic?: string;
  thesisStatement?: string;
  wordCount?: number;
  aiUsagePercentage?: number;
  lastEditedAt?: string;
  collaborators?: string;
  notebookId?: string;
  notebookClassTitle?: string;
  notebookTabId?: string;
  notebookTabLabel?: string;
  notebookDraftId?: string;
  created_at: string;
  updated_at: string;
};

type NotebookContext = {
  notebookId: string;
  notebookTitle: string;
  tabId: string;
  tabLabel: string;
  draftId: string;
};

type User = {
  id: number;
  name: string;
  email: string;
  created_at: string;
  updated_at: string;
};

type ScratchPad = {
  id: number;
  documentId?: number;
  userId?: number;
  title?: string;
  content: string;
  tags?: string;
  position?: number;
  created_at: string;
  updated_at: string;
};

type Collaboration = {
  id: number;
  documentId: number;
  userId: number;
  role: string;
  invitedBy?: number;
  status?: string;
  lastAccessedAt?: string;
  created_at: string;
  updated_at: string;
};

type CollaborationMessage = {
  id: string;
  documentId: number;
  userId: number;
  body: string;
  created_at: string;
};
const DEMO_DOCUMENTS: Document[] = [
  {
    id: 91001,
    title: "SOC 402 Field Immersion",
    content: STYLE_TEMPLATES.APA,
    paperFormat: "APA",
    topic: "Field immersion narrative",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    notebookId: "class-soc402",
    notebookClassTitle: "SOC 402",
    notebookTabId: "discussion",
    notebookTabLabel: "Discussion",
    notebookDraftId: "demo-soc402",
  },
];

const DEMO_USERS: User[] = [
  {
    id: 301,
    name: "Jordan Vega",
    email: "jordan@thesiscribe.demo",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const DEMO_CITATIONS: Citation[] = [
  {
    id: 5001,
    documentId: DEMO_DOCUMENTS[0].id,
    citationType: "book",
    authors: "Smith, A.",
    title: "Community Narratives",
    year: "2022",
    publisher: "Scholars Press",
    url: "https://example.com/community",
    doi: "",
    pages: "12-34",
    volume: "18",
    issue: "2",
    inTextLocations: "",
    formattedReference: "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 5002,
    documentId: DEMO_DOCUMENTS[0].id,
    citationType: "journal",
    authors: "Lopez, R.",
    title: "Urban Solidarity",
    year: "2021",
    publisher: "Journal of Civic Studies",
    url: "https://example.com/solidarity",
    doi: "10.1234/jcs.2021.001",
    pages: "101-118",
    volume: "9",
    issue: "1",
    inTextLocations: "",
    formattedReference: "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const DEMO_SCRATCHPADS: ScratchPad[] = [
  {
    id: 7001,
    documentId: DEMO_DOCUMENTS[0].id,
    userId: DEMO_USERS[0].id,
    title: "Belonging quote",
    content: "Participant B: 'The cohort dinners felt like a lifeline mid-semester.'",
    tags: "[]",
    position: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 7002,
    documentId: DEMO_DOCUMENTS[0].id,
    userId: DEMO_USERS[0].id,
    title: "Data note",
    content: "Need to triangulate observation notes with LMS check-ins.",
    tags: "[]",
    position: 2,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const sanitizeOptionalText = (value?: string | null) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const DEMO_UPLOADS: DemoUploadRecord[] = [
  {
    id: "upl-1",
    fileName: "FocusGroup_transcript.docx",
    fileType: "DOCX",
    wordCount: 1850,
    uploadedAt: new Date().toISOString(),
    status: "parsed",
    summary: "Transcript covering sensemaking themes from cohort two.",
  },
];

const DEMO_COLLABORATIONS: Collaboration[] = [
  {
    id: 8001,
    documentId: DEMO_DOCUMENTS[0].id,
    userId: 401,
    role: "owner",
    status: "accepted",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 8002,
    documentId: DEMO_DOCUMENTS[0].id,
    userId: 402,
    role: "editor",
    status: "accepted",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const DEMO_MESSAGES: CollaborationMessage[] = [
  {
    id: "msg-1",
    documentId: DEMO_DOCUMENTS[0].id,
    userId: 402,
    body: "Uploading the clinic transcript now.",
    created_at: new Date().toISOString(),
  },
];

type UploadedDocument = {
  id: number;
  userId: number;
  fileName: string;
  fileUrl: string;
  created_at: string;
  updated_at: string;
};

type OrganizerTone = "experiential" | "assignment" | "meeting";

type OrganizerItem = {
  id: number;
  title: string;
  tone: OrganizerTone;
  course?: string;
  instructor?: string;
  dueDate?: string;
  notes?: string;
  thesisSummary?: string;
  linkedDocumentId?: number;
  created_at: string;
  updated_at: string;
};

const FALLBACK_ORGANIZER_SUMMARY = {
  course: "SOC 402",
  instructor: "Dr. Ellis",
  nextItemLabel: "Discussion Board 3",
  dueDisplay: "Jan 7 • 11:59 PM",
  upcomingCount: 3,
};

const FALLBACK_ORGANIZER_CHIPS: { id: string; label: string; tone: OrganizerTone }[] = [
  { id: "chip-1", label: "Experiential Log #2", tone: "experiential" },
  { id: "chip-2", label: "Field Notes Review", tone: "meeting" },
  { id: "chip-3", label: "Assignment Draft", tone: "assignment" },
];

const buildDemoOrganizerItem = (
  id: number,
  overrides: Partial<OrganizerItem> = {}
): OrganizerItem => ({
  id,
  title: "Discussion Board 3",
  tone: "assignment",
  course: "SOC 402",
  instructor: "Dr. Ellis",
  dueDate: new Date(Date.now() + id * 86400000).toISOString(),
  notes: "",
  thesisSummary: "",
  linkedDocumentId: DEMO_DOCUMENTS[0].id,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  ...overrides,
});

const DEMO_ORGANIZER_ITEMS: OrganizerItem[] = [
  buildDemoOrganizerItem(1, { title: "Discussion Board 3", tone: "assignment" }),
  buildDemoOrganizerItem(2, { title: "Fieldwork Reflection", tone: "experiential" }),
  buildDemoOrganizerItem(3, { title: "Advisor Check-in", tone: "meeting" }),
];

const normalizeOrganizerTone = (tone?: string): OrganizerTone => {
  if (tone === "experiential" || tone === "assignment" || tone === "meeting") {
    return tone;
  }
  return "assignment";
};

const formatOrganizerDueDisplay = (dueDate?: string): string => {
  if (!dueDate) return "Date TBD";
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) {
    return dueDate;
  }
  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  });
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
};

/* ---------------- Main component ------------------- */
const EditorPage: React.FC = () => {
  const { theme, editorMode } = useTheme();        // use global editorMode
  const navigate = useNavigate();
  const location = useLocation();

  const [content, setContent] = useState(DEMO_DOCUMENTS[0].content);
  const [formatStyle, setFormatStyle] = useState<DocumentFormat>(DEMO_DOCUMENTS[0].paperFormat);
  const [title, setTitle] = useState(DEMO_DOCUMENTS[0].title);
  const [activeRightTab, setActiveRightTab] = useState("citations");
  const [sidebarWidth] = useState(320);
  const [activeDocumentId, setActiveDocumentId] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notebookContext, setNotebookContext] = useState<NotebookContext | null>(null);
  const [localDraftId, setLocalDraftId] = useState<string | null>(null);
  const [demoCitations, setDemoCitations] = useState<Citation[]>(DEMO_CITATIONS);
  const [demoScratchPads, setDemoScratchPads] = useState<ScratchPad[]>(DEMO_SCRATCHPADS);
  const [demoUploads, setDemoUploads] = useState<DemoUploadRecord[]>(DEMO_UPLOADS);
  const [demoCollaborators, setDemoCollaborators] = useState<Collaboration[]>(DEMO_COLLABORATIONS);
  const [demoMessages, setDemoMessages] = useState<CollaborationMessage[]>(DEMO_MESSAGES);
  const [aiUsage, setAiUsage] = useState(0);
  const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
  const [musicDockSignal, setMusicDockSignal] = useState(0);
  const [dictationSupported, setDictationSupported] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [dictationStatus, setDictationStatus] = useState<string | null>(null);
  const [headerText, setHeaderText] = useState("SOC 402 • Field Immersion");
  const [footerText, setFooterText] = useState("Jordan Vega • Thesiscribe");
  const [showPageNumbers, setShowPageNumbers] = useState(true);
  const [visiblePage, setVisiblePage] = useState(1);
  const [marginPreset, setMarginPreset] = useState("standard");
  const [paperTopic, setPaperTopic] = useState(DEMO_DOCUMENTS[0].topic || "");
  const [thesisStatement, setThesisStatement] = useState("");
  const [thesisSyncState, setThesisSyncState] = useState<{ status: "idle" | "saving" | "success" | "error"; message?: string }>({ status: "idle" });
  const [demoOrganizerLinks, setDemoOrganizerLinks] = useState<Record<string, string>>({});
  const [thesisRefinement, setThesisRefinement] = useState<ThesisRefinementResult | null>(null);
  const [refiningThesis, setRefiningThesis] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<null | "pdf" | "docx">(null);
  const [measuredPageCount, setMeasuredPageCount] = useState(1);
  const [snackPanelOpen, setSnackPanelOpen] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>(() => [
    {
      id: "assistant-welcome",
      role: "assistant",
      content: "Hi! I'm your Thesiscribe copilot. Ask me to open tools, summarize your draft, or brainstorm steps while you keep typing.",
      variant: "chat",
      timestamp: Date.now(),
    },
  ]);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [assistantError, setAssistantError] = useState<string | null>(null);

  const quillRef = useRef<any>(null);
  const inlineReferenceRegistry = useRef<Record<number, Partial<Record<InlineVariantKey, string>>>>({});
  const printableAreaRef = useRef<HTMLDivElement | null>(null);
  const editorScrollRef = useRef<HTMLDivElement | null>(null);
  const speechRecognitionRef = useRef<any>(null);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const showDemoPanels = searchParams.get("demo") === "true";
  const documentIdParam = searchParams.get("documentId");
  const notebookIdParam = searchParams.get("notebookId");
  const notebookTitleParam = searchParams.get("notebookTitle");
  const notebookTabIdParam = searchParams.get("tabId");
  const notebookTabLabelParam = searchParams.get("tabLabel");
  const notebookDraftIdParam = searchParams.get("draftId");

  // entities
  const { items: citations, create: createCitation } = useEntity<Citation>(citationEntityConfig);
  const { items: documents, create: createDocument, update: updateDocument } =
    useEntity<Document>(documentEntityConfig);
  const { items: users } = useEntity<User>(userEntityConfig);
  const { items: scratchPads, create: createScratchPadEntry } = useEntity<ScratchPad>(scratchPadEntityConfig);
  const {
    items: snackSpots,
    loading: snackLoading,
    create: createSnackSpot,
    update: updateSnackSpot,
    remove: deleteSnackSpot,
  } = useEntity<SnackSpotRecord>(snackSpotEntityConfig);
  const { items: collaborations } =
    useEntity<Collaboration>(collaborationEntityConfig);
  const { items: uploadedDocuments } =
    useEntity<UploadedDocument>(uploadedDocumentEntityConfig);
  const { items: organizerItems, update: updateOrganizerItem } = useEntity<OrganizerItem>(
    organizerItemEntityConfig
  );

  const activeDocument = useMemo(() => {
    if (activeDocumentId) {
      return (
        documents.find((doc) => doc.id === activeDocumentId) ||
        DEMO_DOCUMENTS.find((doc) => doc.id === activeDocumentId) ||
        documents[0] ||
        DEMO_DOCUMENTS[0]
      );
    }
    return documents[0] || DEMO_DOCUMENTS[0];
  }, [documents, activeDocumentId]);

  const currentUser = users[0] || DEMO_USERS[0];
  const [titlePageFields, setTitlePageFields] = useState<TitlePageFields>(() => {
    const defaults = createDefaultTitleFields();
    return {
      ...defaults,
      studentName: currentUser?.name || defaults.studentName,
    };
  });
  const documentIdForPanels = activeDocument?.id ?? DEMO_DOCUMENTS[0].id;
  const availableDocuments = documents.length ? documents : DEMO_DOCUMENTS;

  useEffect(() => {
    inlineReferenceRegistry.current = {};
  }, [documentIdForPanels]);

  const usingDemoCitations = citations.length === 0;
  const citationSource = usingDemoCitations ? demoCitations : citations;
  const usingDemoScratchpads = scratchPads.length === 0;
  const scratchCollection = usingDemoScratchpads ? demoScratchPads : scratchPads;
  const usingDemoCollaborators = showDemoPanels && collaborations.length === 0;
  const usingDemoOrganizer = organizerItems.length === 0;

  const uploadedFromDb = useMemo(() =>
    uploadedDocuments.map((doc) => ({
      id: `upl-db-${doc.id}`,
      fileName: doc.fileName,
      fileType: doc.fileName.split(".").pop()?.toUpperCase() || "DOC",
      wordCount: 0,
      uploadedAt: doc.created_at,
      status: "parsed" as const,
      summary: doc.fileUrl,
    })),
  [uploadedDocuments]);

  const uploadsForPanel = uploadedFromDb.length ? uploadedFromDb : demoUploads;
  const usingDemoUploads = uploadedFromDb.length === 0;

  useEffect(() => {
    if (currentUser?.name) {
      setTitlePageFields((prev) => ({ ...prev, studentName: currentUser.name }));
    }
  }, [currentUser?.name]);

  const wordCount = content
    .replace(/&lt;[^&gt;]*>/g, "")
    .split(/\s+/)
    .filter(Boolean).length;
  const manualPageBreaks = useMemo(() => (content.match(/ql-page-break/g) || []).length, [content]);
  const estimatedPageCount = Math.max(
    measuredPageCount,
    1 + manualPageBreaks,
    Math.ceil(wordCount / 450)
  );

  const docCitations = useMemo(
    () => citationSource.filter((ref) => ref.documentId === documentIdForPanels),
    [citationSource, documentIdForPanels]
  );

  const assistantConversation = useMemo<AssistantConversationMessage[]>(
    () =>
      assistantMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      })),
    [assistantMessages]
  );


  const PAGE_HEIGHT_PX = 1056; // 11 inches at 96px per inch
  const marginPresets: Record<string, { label: string; value: number }> = {
    narrow: { label: "Narrow (0.75\" margins)", value: 54 },
    standard: { label: "Standard (1\" margins)", value: 72 },
    spacious: { label: "Wide (1.25\" margins)", value: 90 },
  };

  const activeMargin = marginPresets[marginPreset] || marginPresets.standard;
  const headerOverlaySpace = headerText ? 64 : 0;
  const footerOverlaySpace = footerText || showPageNumbers ? 48 : 0;
  const editorPaddingTop = activeMargin.value + headerOverlaySpace;
  const editorPaddingBottom = activeMargin.value + footerOverlaySpace;
  const titlePagePreviewLines = useMemo(
    () => buildTitlePageLines(formatStyle, titlePageFields, title || "Paper Title"),
    [formatStyle, titlePageFields, title]
  );

  const insertPlainText = useCallback(
    (text: string) => {
      if (!text) return;
      const quill = quillRef.current?.getEditor?.();
      if (quill) {
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertText(index, text);
        quill.setSelection(index + text.length, 0);
      } else {
        setContent((prev) => `${prev}${text}`);
      }
      setHasChanges(true);
    },
    [setContent]
  );

  const handleThesisTextChange = useCallback((text: string) => {
    setThesisStatement(text);
    setHasChanges(true);
  }, [setHasChanges]);

  const registerInlineVariant = useCallback(
    (referenceId: number, variant: InlineVariantKey, renderedText: string) => {
      if (!renderedText) return;
      inlineReferenceRegistry.current[referenceId] = inlineReferenceRegistry.current[referenceId] || {};
      inlineReferenceRegistry.current[referenceId][variant] = renderedText;
    },
    []
  );

  const makeAssistantMessage = useCallback(
    (role: "assistant" | "user", content: string, variant: "chat" | "command" = "chat"): AssistantMessage => ({
      id: `ai-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role,
      content,
      variant,
      timestamp: Date.now(),
    }),
    []
  );

  const appendAssistantMessage = useCallback(
    (role: "assistant" | "user", content: string, variant: "chat" | "command" = "chat") => {
      const next = makeAssistantMessage(role, content, variant);
      setAssistantMessages((prev) => [...prev, next]);
      return next;
    },
    [makeAssistantMessage]
  );


  const replaceTrackedText = useCallback((target: string, replacement: string) => {
    if (!target || !replacement || target === replacement) return;
    let didReplace = false;
    setContent((prev) => {
      if (!prev.includes(target)) {
        return prev;
      }
      didReplace = true;
      return prev.split(target).join(replacement);
    });
    if (didReplace) {
      setHasChanges(true);
    }
  }, [setContent, setHasChanges]);

  const handleInsertLinkedReference = useCallback(
    (ref: ReferenceLike, variant: InlineVariantKey, renderedText: string) => {
      if (!renderedText) return;
      registerInlineVariant(ref.id, variant, renderedText);
      insertPlainText(renderedText);
    },
    [insertPlainText, registerInlineVariant]
  );

  const handleReferenceUpdated = useCallback(
    (ref: ReferenceLike) => {
      if (!ref?.id) return;
      const tracked = inlineReferenceRegistry.current[ref.id];
      if (!tracked) return;
      const freshVariants = buildInlineReferenceVariants(ref, formatStyle);
      (Object.keys(tracked) as InlineVariantKey[]).forEach((variantKey) => {
        const previousText = tracked[variantKey];
        const nextText = freshVariants[variantKey];
        if (previousText && nextText && previousText !== nextText) {
          replaceTrackedText(previousText, nextText);
          tracked[variantKey] = nextText;
        }
      });
    },
    [formatStyle, replaceTrackedText]
  );

  const handleReferenceDeleted = useCallback((referenceId: number) => {
    delete inlineReferenceRegistry.current[referenceId];
  }, []);

  useEffect(() => {
    docCitations.forEach((ref) => handleReferenceUpdated(ref));
  }, [docCitations, handleReferenceUpdated]);

  useEffect(() => {
    if (thesisSyncState.status === "success" || thesisSyncState.status === "error") {
      const timeout = window.setTimeout(() => setThesisSyncState({ status: "idle" }), 2500);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [thesisSyncState.status]);

  const handleInsertCitation = (citationText: string) => {
    insertPlainText(citationText);
  };

  const handleInsertThesis = (text: string) => {
    insertPlainText(`${text}\n\n`);
    handleThesisTextChange(text);
  };

  const handleRefineThesis = useCallback(async () => {
    if (!thesisStatement.trim()) return;
    setRefiningThesis(true);
    try {
      const result = await refineThesisStatement({
        thesis: thesisStatement,
        topic: paperTopic,
        documentExcerpt: content.replace(/<[^>]*>/g, "").slice(0, 2000),
      });
      setThesisRefinement(result);
      if (result?.improvedText) {
        handleThesisTextChange(result.improvedText);
      }
    } catch (error) {
      console.error("Failed to refine thesis", error);
    } finally {
      setRefiningThesis(false);
    }
  }, [thesisStatement, paperTopic, content, handleThesisTextChange]);

  const handleSyncThesisToOrganizer = useCallback(async () => {
    if (!thesisStatement.trim()) {
      setThesisSyncState({ status: "error", message: "Write or paste a thesis before syncing." });
      return;
    }
    setThesisSyncState({ status: "saving" });
    try {
      if (usingDemoOrganizer || !activeDocumentId) {
        setDemoOrganizerLinks((prev) => ({ ...prev, demo: thesisStatement }));
      } else {
        const matchingItem = organizerItems.find((item) => item.linkedDocumentId === activeDocumentId);
        if (!matchingItem) {
          setThesisSyncState({ status: "error", message: "Link this draft to an organizer item to sync." });
          return;
        }
        await updateOrganizerItem(matchingItem.id, { thesisSummary: thesisStatement });
      }
      setThesisSyncState({ status: "success", message: "Thesis synced." });
    } catch (error) {
      console.error("Failed syncing thesis", error);
      setThesisSyncState({ status: "error", message: "Could not sync thesis. Try again." });
    }
  }, [thesisStatement, usingDemoOrganizer, activeDocumentId, organizerItems, updateOrganizerItem]);

  const handleScratchPadCreate = (entry: ScratchPad) => {
    setDemoScratchPads((prev) => {
      const others = prev.filter((item) => item.id !== entry.id);
      return [...others, entry].sort((a, b) => {
        const aPos = a.position ?? (a.created_at ? Date.parse(a.created_at) : 0);
        const bPos = b.position ?? (b.created_at ? Date.parse(b.created_at) : 0);
        return (aPos || 0) - (bPos || 0);
      });
    });
  };

  const handleScratchPadDelete = (entryId: number) => {
    setDemoScratchPads((prev) => prev.filter((entry) => entry.id !== entryId));
  };

  const handleScratchPadUpdate = (nextEntry: ScratchPad) => {
    setDemoScratchPads((prev) =>
      prev.map((entry) => (entry.id === nextEntry.id ? nextEntry : entry))
    );
  };

  const handleScratchPadReorder = useCallback((entryId: number, targetId: number) => {
    setDemoScratchPads((prev) => {
      const next = [...prev];
      const fromIndex = next.findIndex((entry) => entry.id === entryId);
      const toIndex = next.findIndex((entry) => entry.id === targetId);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next.map((entry, idx) => ({ ...entry, position: idx + 1 }));
    });
  }, []);

  const handleCreateSnackSpot = useCallback(
    async (payload: SnackSpotDraft) => {
      const label = payload.label.trim();
      if (!label) return;
      await createSnackSpot({
        userId: currentUser.id,
        label,
        phone: sanitizeOptionalText(payload.phone),
        deliveryUrl: sanitizeOptionalText(payload.deliveryUrl),
        locationHint: sanitizeOptionalText(payload.locationHint),
        notes: sanitizeOptionalText(payload.notes),
        favorite: payload.favorite ? 1 : 0,
      } as Omit<SnackSpotRecord, "id" | "created_at" | "updated_at">);
    },
    [createSnackSpot, currentUser.id]
  );

  const handleUpdateSnackSpot = useCallback(
    async (id: number, changes: Partial<SnackSpotRecord>) => {
      await updateSnackSpot(id, changes);
    },
    [updateSnackSpot]
  );

  const handleDeleteSnackSpot = useCallback(
    async (id: number) => {
      await deleteSnackSpot(id);
    },
    [deleteSnackSpot]
  );

  const handleMarkSnackOrdered = useCallback(
    async (id: number) => {
      await updateSnackSpot(id, { lastOrderedAt: new Date().toISOString() });
    },
    [updateSnackSpot]
  );

  const handleUploadRecord = (record: DemoUploadRecord) => {
    setDemoUploads((prev) => [record, ...prev]);
  };

  const handleImportUploadContent = (snippet: string) => {
    if (!snippet) return;
    insertPlainText(`\n${snippet}\n`);
  };

  const handleSaveSuggestionToScratchPad = useCallback(
    async ({ title: rawTitle, content: rawContent }: { title?: string; content: string }) => {
      const contentToSave = rawContent.trim();
      if (!contentToSave) return;
      const normalizedTitle = (rawTitle || "Topic suggestion").trim() || "Topic suggestion";
      const timestamp = new Date().toISOString();
      const position = Date.now();

      if (usingDemoScratchpads) {
        setDemoScratchPads((prev) => [
          ...prev,
          {
            id: Date.now(),
            documentId: documentIdForPanels,
            userId: currentUser.id,
            title: normalizedTitle,
            content: contentToSave,
            tags: JSON.stringify(["topic-assistant"]),
            position,
            created_at: timestamp,
            updated_at: timestamp,
          },
        ]);
        return;
      }

      await createScratchPadEntry({
        documentId: documentIdForPanels,
        userId: currentUser.id,
        title: normalizedTitle,
        content: contentToSave,
        tags: JSON.stringify(["topic-assistant"]),
        position,
      });
    },
    [usingDemoScratchpads, documentIdForPanels, currentUser.id, createScratchPadEntry]
  );

  const scrollToPage = useCallback(
    (pageNumber: number) => {
      const node = editorScrollRef.current;
      if (!node) return;
      const clampedIndex = Math.max(0, Math.min(pageNumber, estimatedPageCount - 1));
      node.scrollTo({ top: clampedIndex * PAGE_HEIGHT_PX, behavior: "smooth" });
    },
    [estimatedPageCount, PAGE_HEIGHT_PX]
  );

  const handleTitlePageFieldChange = (key: keyof TitlePageFields, value: string) => {
    setTitlePageFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleInsertFormattedTitlePage = () => {
    const formatted = formatTitlePageText(formatStyle, titlePageFields, title || "Paper Title");
    if (!formatted.trim()) return;
    const block = `${formatted}\n\n`;
    const quill = quillRef.current?.getEditor?.();
    if (quill) {
      quill.insertText(0, block, "user");
      quill.setSelection(block.length, 0);
    } else {
      setContent((prev) => `${block}${prev}`);
    }
    scrollToPage(0);
    setHasChanges(true);
  };

  const handleAddSourceToDocument = (source: any) => {
    if (!source) return;
    const citationLine = [
      source.authors || "Unknown author",
      source.year ? `(${source.year})` : "(n.d.)",
      source.title || "Untitled work",
      source.publisher || source.source_type || "",
      source.url || "",
    ]
      .filter(Boolean)
      .join(". ");
    insertPlainText(`\n${citationLine.trim()}\n`);
  };

  const handleInsertPageBreak = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();
    quill.insertEmbed(index, "page-break", true, "user");
    quill.insertText(index + 1, "\n", "user");
    quill.setSelection(index + 2, 0);
    setHasChanges(true);
  }, []);

  const handleAddCitationFromAdvisor = async (rawCitation: any) => {
    const authorField = Array.isArray(rawCitation?.authors)
      ? rawCitation.authors
          .map((auth: any) => {
            if (typeof auth === "string") return auth;
            return `${auth?.first_name ?? ""} ${auth?.last_name ?? ""}`.trim();
          })
          .filter(Boolean)
          .join(", ")
      : rawCitation?.authors || "";

    const normalizedBase = {
      documentId: documentIdForPanels,
      citationType: rawCitation?.citationType || rawCitation?.citation_type || "journal",
      authors: authorField,
      title: rawCitation?.title || "Untitled Source",
      year: rawCitation?.year || "n.d.",
      publisher: rawCitation?.publisher || rawCitation?.source || "",
      url: rawCitation?.url || "",
      doi: rawCitation?.doi || "",
      pages: rawCitation?.pages || "",
      volume: rawCitation?.volume || "",
      issue: rawCitation?.issue || "",
      inTextLocations: "",
      formattedReference: "",
    };

    if (usingDemoCitations) {
      setDemoCitations((prev) => [
        ...prev,
        {
          id: Date.now(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          ...normalizedBase,
        } as Citation,
      ]);
    } else {
      await createCitation(normalizedBase as Omit<Citation, "id" | "created_at" | "updated_at">);
    }
  };

  useEffect(() => {
    const node = editorScrollRef.current;
    if (!node) return;
    const updateVisiblePage = () => {
      const nextPage = Math.min(
        estimatedPageCount,
        Math.max(1, Math.floor(node.scrollTop / PAGE_HEIGHT_PX) + 1)
      );
      setVisiblePage(nextPage);
    };
    updateVisiblePage();
    node.addEventListener("scroll", updateVisiblePage);
    return () => {
      node.removeEventListener("scroll", updateVisiblePage);
    };
  }, [estimatedPageCount, PAGE_HEIGHT_PX]);

  useEffect(() => {
    setVisiblePage((prev) => Math.min(prev, estimatedPageCount));
  }, [estimatedPageCount]);

  useEffect(() => {
    const node = printableAreaRef.current;
    if (!node) return;
    const computePages = () => {
      const totalHeight = node.scrollHeight || node.clientHeight || PAGE_HEIGHT_PX;
      setMeasuredPageCount(Math.max(1, Math.ceil(totalHeight / PAGE_HEIGHT_PX)));
    };

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(computePages);
      observer.observe(node);
    } else {
      computePages();
    }

    computePages();

    return () => {
      observer?.disconnect();
    };
  }, [content, editorPaddingTop, editorPaddingBottom, PAGE_HEIGHT_PX]);

  const handleInviteCollaborator = ({ email, role }: { email: string; role: "editor" | "commenter" | "viewer" }) => {
    const timestamp = new Date().toISOString();
    const newCollab: Collaboration = {
      id: Date.now(),
      documentId: documentIdForPanels,
      userId: Math.floor(Math.random() * 1000) + 500,
      role,
      invitedBy: currentUser.id,
      status: "pending",
      lastAccessedAt: timestamp,
      created_at: timestamp,
      updated_at: timestamp,
    };
    setDemoCollaborators((prev) => [...prev, newCollab]);
    setDemoMessages((prev) => [
      {
        id: `invite-${Date.now()}`,
        documentId: documentIdForPanels,
        userId: currentUser.id,
        body: `Invited ${email} as ${role}.`,
        created_at: timestamp,
      },
      ...prev,
    ]);
  };

  const handleRemoveCollaborator = (id: number) => {
    setDemoCollaborators((prev) => prev.filter((collab) => collab.id !== id));
  };

  const handleSendCollabMessage = ({ body }: { body: string }) => {
    if (!body.trim()) return;
    const message: CollaborationMessage = {
      id: `chat-${Date.now()}`,
      documentId: documentIdForPanels,
      userId: currentUser.id,
      body: body.trim(),
      created_at: new Date().toISOString(),
    };
    setDemoMessages((prev) => [message, ...prev].slice(0, 50));
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const SpeechRecognitionCtor =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      setDictationSupported(false);
      return;
    }
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    speechRecognitionRef.current = recognition;
    setDictationSupported(true);

    recognition.onresult = (event: any) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript ?? "";
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        }
      }
      if (finalTranscript.trim()) {
        insertPlainText(` ${finalTranscript.trim()} `);
      }
    };

    recognition.onerror = (event: any) => {
      setDictationStatus(event.error || "Microphone error");
      setIsDictating(false);
    };

    recognition.onend = () => {
      setIsDictating(false);
      setDictationStatus((prev) => (prev === "Listening..." ? "Dictation paused" : prev));
    };

    return () => {
      recognition.stop();
    };
  }, [insertPlainText]);

  const hydrateFromDocument = useCallback((doc: Document) => {
    setActiveDocumentId(doc.id);
    setTitle(doc.title);
    setContent(doc.content);
    setFormatStyle(doc.paperFormat as DocumentFormat);
    setPaperTopic(doc.topic || "");
    setThesisStatement(doc.thesisStatement || "");
    setHasChanges(false);
    if (doc.notebookDraftId) {
      setLocalDraftId(doc.notebookDraftId);
    }
    if (doc.notebookId && doc.notebookTabId) {
      setNotebookContext({
        notebookId: doc.notebookId,
        notebookTitle: doc.notebookClassTitle || doc.notebookId,
        tabId: doc.notebookTabId,
        tabLabel: doc.notebookTabLabel || doc.notebookTabId,
        draftId: doc.notebookDraftId || `${doc.notebookId}-${doc.notebookTabId}`,
      });
    }
  }, []);

  useEffect(() => {
    if (!notebookIdParam || !notebookTabIdParam || documentIdParam) return;
    setNotebookContext({
      notebookId: notebookIdParam,
      notebookTitle: notebookTitleParam || notebookIdParam,
      tabId: notebookTabIdParam,
      tabLabel: notebookTabLabelParam || notebookTabIdParam,
      draftId: notebookDraftIdParam || `${notebookIdParam}-${notebookTabIdParam}`,
    });
    if (notebookDraftIdParam) {
      setLocalDraftId(notebookDraftIdParam);
    }
  }, [
    documentIdParam,
    notebookDraftIdParam,
    notebookIdParam,
    notebookTabIdParam,
    notebookTabLabelParam,
    notebookTitleParam,
  ]);

  useEffect(() => {
    if (!documentIdParam) return;
    const docId = Number(documentIdParam);
    if (!Number.isFinite(docId)) return;
    if (activeDocumentId === docId) return;
    const doc = documents.find((d) => d.id === docId);
    if (doc) {
      hydrateFromDocument(doc);
    }
  }, [documentIdParam, documents, activeDocumentId, hydrateFromDocument]);

  useEffect(() => {
    if (activeDocumentId || !localDraftId) return;
    const doc = documents.find((d) => d.notebookDraftId === localDraftId);
    if (doc) {
      hydrateFromDocument(doc);
    }
  }, [documents, activeDocumentId, localDraftId, hydrateFromDocument]);

  const handleFormatChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const nextFormat = e.target.value as DocumentFormat;
    setFormatStyle(nextFormat);
    const tpl = STYLE_TEMPLATES[nextFormat] || "";
    setContent(tpl);
    setHasChanges(true);
    const quill = quillRef.current?.getEditor?.();
    if (quill) {
      quill.setContents(quill.clipboard.convert(tpl || ""));
      quill.setSelection(quill.getLength(), 0);
    }
  };

  const handleToggleDictation = () => {
    if (!dictationSupported || !speechRecognitionRef.current) {
      setDictationStatus("Dictation is not supported in this browser.");
      return;
    }
    if (isDictating) {
      speechRecognitionRef.current.stop();
      setIsDictating(false);
      setDictationStatus("Dictation paused");
      return;
    }
    try {
      setDictationStatus("Listening...");
      speechRecognitionRef.current.start();
      setIsDictating(true);
    } catch (error) {
      setDictationStatus("Unable to access microphone");
      setIsDictating(false);
    }
  };

  const handleNewDocument = () => {
    const template = STYLE_TEMPLATES[formatStyle] || "";
    setActiveDocumentId(null);
    setTitle("Untitled Document");
    setContent(template);
     setPaperTopic("");
    setThesisStatement("");
    setNotebookContext(null);
    setLocalDraftId(null);
    setHasChanges(true);
    const quill = quillRef.current?.getEditor?.();
    if (quill) {
      quill.setContents(quill.clipboard.convert(template || ""));
      quill.setSelection(quill.getLength(), 0);
    }
  };

  const handlePrintDocument = () => {
    const originalTitle = document.title;
    document.title = title || "Thesiscribe draft";
    document.body.classList.add("print-mode");
    const cleanup = () => {
      document.body.classList.remove("print-mode");
      document.title = originalTitle;
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  };

  const buildPrintableHtmlShell = () => {
    const printableNode = printableAreaRef.current;
    if (!printableNode) return null;
    const printableHtml = printableNode.innerHTML;
    return `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>
      body { font-family: 'Times New Roman', serif; font-size: 12pt; }
      .ql-page-break { page-break-after: always; border: none; }
    </style></head><body>${printableHtml}</body></html>`;
  };

  const handleExportDocument = async (format: "pdf" | "docx") => {
    const printableNode = printableAreaRef.current;
    if (!printableNode) return;
    setExportingFormat(format);
    try {
      const htmlShell = buildPrintableHtmlShell();
      if (!htmlShell) return;

      if (isCloudConvertEnabled) {
        const safeName = sanitizeFileName(`${title || "thesis"}-${formatStyle}`);
        try {
          const { blob, fileName } = await convertHtmlToFile(format, htmlShell, safeName);
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
          return;
        } catch (cloudError) {
          console.warn("CloudConvert export failed, falling back to local export", cloudError);
        }
      }

      if (format === "pdf") {
        await html2pdf()
          .from(printableNode)
          .set({
            filename: `${title || "thesis"}-${formatStyle}.pdf`,
            margin: activeMargin.value / 96,
            pagebreak: { mode: ["css", "legacy"] },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
          })
          .save();
      } else {
        const htmlDocx = await loadHtmlDocx();
        const blob = htmlDocx.asBlob(htmlShell);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title || "thesis"}.docx`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export failed", error);
      alert("Export failed. Please try again.");
    } finally {
      setExportingFormat(null);
    }
  };

  const handleSelectDocument = (doc: Document) => {
    hydrateFromDocument(doc);
    setDocumentPickerOpen(false);
  };

  const handleShowMusicPlayer = () => {
    setMusicDockSignal((prev) => prev + 1);
  };

  const handleOpenStyleGuide = () => {
    setActiveRightTab("styleguide");
    const guideLink = STYLE_GUIDE_LINKS[formatStyle];
    if (guideLink) {
      window.open(guideLink, "_blank", "noopener,noreferrer");
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: "#editor-toolbar",
        handlers: {
          pageBreak: handleInsertPageBreak,
        },
      },
      history: {
        delay: 500,
        maxStack: 500,
        userOnly: false,
      },
    }),
    [handleInsertPageBreak]
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "blockquote",
    "align",
    "link",
    "image",
    "script",
    "code-block",
  ];

  const resolveDraftId = () => {
    if (notebookContext?.draftId) return notebookContext.draftId;
    if (localDraftId) return localDraftId;
    const generated = `standalone-${Date.now()}`;
    setLocalDraftId(generated);
    return generated;
  };

  const handleSaveDocument = async () => {
    if (saving) return;
    const ownerId = users[0]?.id ?? 1;
    const draftKey = resolveDraftId();
    const basePayload = {
      title,
      content,
      paperFormat: formatStyle,
      userId: ownerId,
      topic: paperTopic,
      thesisStatement,
      wordCount,
      aiUsagePercentage: 0,
      lastEditedAt: new Date().toISOString(),
      collaborators: "[]",
      notebookDraftId: draftKey,
    } as Partial<Document> & {
      title: string;
      content: string;
      paperFormat: DocumentFormat;
      userId: number;
    };

    if (notebookContext) {
      Object.assign(basePayload, {
        notebookId: notebookContext.notebookId,
        notebookClassTitle: notebookContext.notebookTitle,
        notebookTabId: notebookContext.tabId,
        notebookTabLabel: notebookContext.tabLabel,
      });
    }

    try {
      setSaving(true);
      if (activeDocumentId) {
        await updateDocument(activeDocumentId, basePayload);
      } else {
        await createDocument(basePayload as Omit<Document, "id" | "created_at" | "updated_at">);
      }
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Right‑panel tab config ---------- */
  const rightPanelTabs = [
    { id: "citations", icon: BookOpen, label: "References" },
    { id: "advisor", icon: Search, label: "Advisor" },
    { id: "library", icon: Library, label: "Library" },
    { id: "dictionary", icon: Book, label: "Dictionary" },
    { id: "styleguide", icon: BookOpen, label: "Style Guide" },
    { id: "scratchpad", icon: Clipboard, label: "Scratch" },
    { id: "assistant", icon: Lightbulb, label: "Topic" },
    { id: "thesis", icon: PenSquare, label: "Thesis" },
    { id: "originality", icon: Shield, label: "AI Check" },
    { id: "integrations", icon: Link2, label: "Integrations" },
    { id: "collab", icon: Users, label: "Collab" },
    { id: "upload", icon: Upload, label: "Upload" },
    { id: "suggestions", icon: Lightbulb, label: "Suggestions" },
  ];

  const citationTabs = [
    { id: "citations", icon: BookOpen, label: "References" },
    { id: "advisor", icon: Search, label: "Advisor" },
    { id: "suggestions", icon: Lightbulb, label: "Suggestions" },
    { id: "thesis", icon: PenSquare, label: "Thesis" },
  ];

  const otherTabs = rightPanelTabs.filter(
    (tab) => !citationTabs.find((c) => c.id === tab.id)
  );

  const tabAccents: Record<string, { active: string; inactive: string }> = {
    citations: {
      active: "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white border-white/30 shadow-lg",
      inactive: "text-purple-200 border-white/10 hover:bg-purple-500/10",
    },
    advisor: {
      active: "bg-gradient-to-r from-sky-500 to-cyan-500 text-white border-white/30 shadow-lg",
      inactive: "text-sky-200 border-white/10 hover:bg-sky-500/10",
    },
    suggestions: {
      active: "bg-gradient-to-r from-amber-500 to-orange-500 text-white border-white/30 shadow-lg",
      inactive: "text-amber-200 border-white/10 hover:bg-amber-500/10",
    },
    library: {
      active: "bg-gradient-to-r from-emerald-500 to-lime-500 text-white border-white/30 shadow-lg",
      inactive: "text-emerald-200 border-white/10 hover:bg-emerald-500/10",
    },
    dictionary: {
      active: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-white/30 shadow-lg",
      inactive: "text-indigo-200 border-white/10 hover:bg-indigo-500/10",
    },
    styleguide: {
      active: "bg-gradient-to-r from-rose-500 to-pink-500 text-white border-white/30 shadow-lg",
      inactive: "text-rose-200 border-white/10 hover:bg-rose-500/10",
    },
    scratchpad: {
      active: "bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white border-white/30 shadow-lg",
      inactive: "text-fuchsia-200 border-white/10 hover:bg-fuchsia-500/10",
    },
    assistant: {
      active: "bg-gradient-to-r from-yellow-500 to-amber-500 text-white border-white/30 shadow-lg",
      inactive: "text-yellow-200 border-white/10 hover:bg-yellow-500/10",
    },
    thesis: {
      active: "bg-gradient-to-r from-amber-500 to-rose-500 text-white border-white/30 shadow-lg",
      inactive: "text-amber-200 border-white/10 hover:bg-amber-500/10",
    },
    originality: {
      active: "bg-gradient-to-r from-emerald-600 to-teal-500 text-white border-white/30 shadow-lg",
      inactive: "text-emerald-200 border-white/10 hover:bg-emerald-500/10",
    },
    integrations: {
      active: "bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-white/30 shadow-lg",
      inactive: "text-cyan-200 border-white/10 hover:bg-cyan-500/10",
    },
    collab: {
      active: "bg-gradient-to-r from-green-500 to-emerald-600 text-white border-white/30 shadow-lg",
      inactive: "text-green-200 border-white/10 hover:bg-green-500/10",
    },
    upload: {
      active: "bg-gradient-to-r from-slate-500 to-slate-600 text-white border-white/30 shadow-lg",
      inactive: "text-slate-200 border-white/10 hover:bg-slate-500/10",
    },
  };

  const panelCommandMap: Record<string, string> = {
    "open-scratch": "scratchpad",
    "open-thesis": "thesis",
    "open-library": "library",
    "open-citations": "citations",
    "open-advisor": "advisor",
    "open-suggestions": "suggestions",
    "open-collab": "collab",
    "open-topic": "assistant",
    "open-dictionary": "dictionary",
  };

  const assistantContext = useMemo(
    () => ({
      documentTitle: title || "Untitled Document",
      activePanel: activeRightTab,
      panels: rightPanelTabs.map((tab) => ({ id: tab.id, label: tab.label })),
      thesis: thesisStatement,
      wordCount,
      scratchCount: scratchCollection.length,
    }),
    [title, activeRightTab, rightPanelTabs, thesisStatement, wordCount, scratchCollection.length]
  );

  const assistantContextSummary = useMemo(() => {
    const scratchPreview = scratchCollection
      .slice(0, 2)
      .map((entry) => `${entry.title || "Note"}: ${entry.content?.slice(0, 80) || "(empty)"}`)
      .join(" | ");
    const thesisPreview = thesisStatement ? thesisStatement.slice(0, 140) : "(no thesis yet)";
    return `Title: ${assistantContext.documentTitle}. Active panel: ${assistantContext.activePanel}. Word count: ${assistantContext.wordCount}. Thesis: ${thesisPreview}. Scratch: ${scratchPreview || "none yet"}.`;
  }, [assistantContext, thesisStatement, scratchCollection]);

  const sendAssistantChat = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return null;
      appendAssistantMessage("user", trimmed, "chat");
      setAssistantBusy(true);
      setAssistantError(null);
      try {
        const conversation: AssistantConversationMessage[] = [
          ...assistantConversation,
          { role: "user", content: trimmed },
        ];
        const response = await sendAIAssistChat({
          conversation,
          contextSummary: assistantContextSummary,
        });
        const replyText = response.reply || "I'm here.";
        let finalReply = replyText;
        if (response.suggestions && response.suggestions.length) {
          finalReply += `\n\nNext steps:\n- ${response.suggestions.join("\n- ")}`;
        }
        appendAssistantMessage("assistant", finalReply, "chat");
        return response;
      } catch (error) {
        console.error("assistant chat failed", error);
        const message = error instanceof Error ? error.message : "Unable to reach AI Assist.";
        setAssistantError(message);
        appendAssistantMessage("assistant", "I'm having trouble connecting right now.");
        return null;
      } finally {
        setAssistantBusy(false);
      }
    },
    [appendAssistantMessage, assistantConversation, assistantContextSummary]
  );

  const handleAssistantCommandAction = useCallback(
    async (result: AIAssistCommandResult) => {
      const action = result.action || "noop";
      if (action === "open-aiassist") {
        return;
      }
      if (panelCommandMap[action]) {
        setActiveRightTab(panelCommandMap[action]);
      }

      if (action === "summarize-document") {
        await sendAssistantChat("Summarize the current document concisely.");
      }

      if (action === "summarize-scratch") {
        await sendAssistantChat("Summarize the latest scratch pad notes.");
      }

      if (action === "new-note") {
        if (usingDemoScratchpads) {
          handleScratchPadCreate({
            id: Date.now(),
            title: `Voice Note ${demoScratchPads.length + 1}`,
            content: "",
            created_at: new Date().toISOString(),
          } as ScratchPad);
        } else {
          await createScratchPadEntry({
            title: `AI Note ${scratchPads.length + 1}`,
            content: "",
            documentId: documentIdForPanels,
            userId: currentUser.id,
            position: scratchPads.length + 1,
          } as any);
        }
        setActiveRightTab("scratchpad");
      }
    },
    [panelCommandMap, sendAssistantChat, usingDemoScratchpads, handleScratchPadCreate, demoScratchPads.length, createScratchPadEntry, scratchPads.length, documentIdForPanels, currentUser.id]
  );

  const runAssistantCommand = useCallback(
    async (utterance: string) => {
      const trimmed = utterance.trim();
      if (!trimmed) return;
      appendAssistantMessage("user", trimmed, "command");
      setAssistantBusy(true);
      setAssistantError(null);
      try {
        const result = await requestAIAssistCommand({
          utterance: trimmed,
          context: assistantContext,
        });
        appendAssistantMessage("assistant", result.narration || "Done.", "command");
        await handleAssistantCommandAction(result);
        return result;
      } catch (error) {
        console.error("assistant command failed", error);
        const message = error instanceof Error ? error.message : "Command failed";
        setAssistantError(message);
        appendAssistantMessage("assistant", "I couldn't complete that request just now.", "command");
        return null;
      } finally {
        setAssistantBusy(false);
      }
    },
    [appendAssistantMessage, assistantContext, handleAssistantCommandAction]
  );

  const handleAssistantQuickCommand = useCallback(
    (prompt: string) => {
      runAssistantCommand(prompt);
    },
    [runAssistantCommand]
  );

  const getTabButtonClass = (tabId: string, isActive: boolean) => {
    const accent = tabAccents[tabId] || {
      active: "bg-white/20 text-white border-white/30",
      inactive: "text-white/70 border-white/10 hover:bg-white/10",
    };
    return (
      "flex flex-col items-center justify-center gap-1 px-2 py-1 rounded text-xs border transition-all duration-200 " +
      (isActive ? accent.active : accent.inactive)
    );
  };

  const organizerDisplayItems = useMemo(() => {
    if (usingDemoOrganizer) {
      const demoThesis = demoOrganizerLinks["demo"];
      return DEMO_ORGANIZER_ITEMS.map((item, index) =>
        index === 0 && demoThesis
          ? { ...item, thesisSummary: demoThesis }
          : item
      );
    }
    return organizerItems;
  }, [usingDemoOrganizer, organizerItems, demoOrganizerLinks]);

  const organizerSummary = useMemo(() => {
    if (!organizerDisplayItems.length) {
      return FALLBACK_ORGANIZER_SUMMARY;
    }
    const primary = organizerDisplayItems[0];
    return {
      course: primary.course || FALLBACK_ORGANIZER_SUMMARY.course,
      instructor: primary.instructor || FALLBACK_ORGANIZER_SUMMARY.instructor,
      nextItemLabel: primary.title || FALLBACK_ORGANIZER_SUMMARY.nextItemLabel,
      dueDisplay:
        formatOrganizerDueDisplay(primary.dueDate) ||
        FALLBACK_ORGANIZER_SUMMARY.dueDisplay,
      upcomingCount: organizerDisplayItems.length,
    };
  }, [organizerDisplayItems]);

  const organizerTaskChips = useMemo(() => {
    if (!organizerDisplayItems.length) {
      return FALLBACK_ORGANIZER_CHIPS;
    }
    return organizerDisplayItems.slice(0, 3).map((item, index) => ({
      id: `chip-${item.id ?? index}`,
      label: item.title,
      tone: normalizeOrganizerTone(item.tone),
    }));
  }, [organizerDisplayItems]);

  /* ---------- Right‑panel renderer ---------- */
  function renderRightPanelContent(): JSX.Element | null {
    const panelContent = (() => {
      switch (activeRightTab) {
        case "citations":
          return (
            <ReferenceListPanel
              documentId={documentIdForPanels}
              paperFormat={formatStyle}
              onInsertCitation={handleInsertCitation}
              referencesOverride={usingDemoCitations ? citationSource : undefined}
              onReferencesChange={usingDemoCitations ? setDemoCitations : undefined}
              onInsertLinkedReference={handleInsertLinkedReference}
              onReferenceUpdated={handleReferenceUpdated}
              onReferenceDeleted={handleReferenceDeleted}
            />
          );
        case "advisor":
          return (
            <CitationAdvisorPanel
              documentId={documentIdForPanels}
              documentContent={content}
              formatStyle={formatStyle}
              citations={docCitations}
              onInsertCitation={handleInsertCitation}
              onAddCitation={handleAddCitationFromAdvisor}
            />
          );
        case "suggestions":
          return (
            <CitationSuggestionPanel
              documentId={documentIdForPanels}
              paperFormat={formatStyle}
              documentText={content.replace(/<[^>]*>/g, "")}
              onInsertCitation={handleInsertCitation}
              referencesOverride={usingDemoCitations ? citationSource : undefined}
            />
          );
        case "library":
          return (
            <LibraryPanel
              documentId={documentIdForPanels}
              onAddToDocument={handleAddSourceToDocument}
            />
          );
        case "dictionary":
          return <DictionaryPanel initialWord={paperTopic.split(" ")[0] || ""} />;
        case "scratchpad":
          return (
            <ScratchPadPanel
              documentId={documentIdForPanels}
              userId={currentUser.id}
              onInsertText={insertPlainText}
              entries={usingDemoScratchpads ? demoScratchPads : undefined}
              onCreateEntry={usingDemoScratchpads ? handleScratchPadCreate : undefined}
              onRemoveEntry={usingDemoScratchpads ? handleScratchPadDelete : undefined}
              onReorderEntry={usingDemoScratchpads ? handleScratchPadReorder : undefined}
              onUpdateEntry={usingDemoScratchpads ? handleScratchPadUpdate : undefined}
            />
          );
        case "originality":
          return (
            <AIMonitor
              content={content.replace(/<[^>]*>/g, "")}
              onAIUsageUpdate={setAiUsage}
            />
          );
        case "styleguide": {
          const guideLink = STYLE_GUIDE_LINKS[formatStyle];
          return (
            <div className="h-full w-full bg-white/5 rounded-b-xl px-4 py-3 overflow-auto text-sm text-slate-200">
              <h3 className="font-semibold text-white mb-1">
                {formatStyle} Sample Paper
              </h3>
              {guideLink && (
                <button
                  type="button"
                  onClick={() => window.open(guideLink, "_blank", "noopener,noreferrer")}
                  className="text-[11px] mb-3 inline-flex items-center gap-2 text-purple-200 hover:text-white"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> View official {formatStyle} guide
                </button>
              )}
              <div
                className="prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: STYLE_TEMPLATES[formatStyle],
                }}
              />
            </div>
          );
        }
        case "assistant":
          return (
            <TopicAssistant
              content={content.replace(/<[^>]*>/g, "")}
              topic={paperTopic}
              onTopicChange={setPaperTopic}
              onSaveSuggestion={handleSaveSuggestionToScratchPad}
            />
          );
        case "thesis":
          return (
            <ThesisStatementPanel
              currentDraft={content.replace(/<[^>]*>/g, "")}
              initialThesis={thesisStatement}
              onCopyThesis={(text) => {
                if (!text) return;
                navigator.clipboard.writeText(text).catch(() => {});
                handleThesisTextChange(text);
              }}
              onInsertThesis={handleInsertThesis}
              onSyncThesis={handleSyncThesisToOrganizer}
              syncState={thesisSyncState}
              onThesisChange={handleThesisTextChange}
              onRequestRefine={handleRefineThesis}
              refining={refiningThesis}
              refinementResult={thesisRefinement}
            />
          );
        case "integrations":
          return <IntegrationsPanel />;
        case "upload":
          return (
            <UploadPanel
              uploads={uploadsForPanel}
              onUploadRecord={usingDemoUploads ? handleUploadRecord : () => {}}
              onImportToEditor={handleImportUploadContent}
            />
          );
        case "collab":
          return (
            <CollaborationPanel
              documentId={documentIdForPanels}
              currentUserId={currentUser.id}
              collaborators={usingDemoCollaborators ? demoCollaborators : undefined}
              onInviteCollaborator={usingDemoCollaborators ? handleInviteCollaborator : undefined}
              onRemoveCollaborator={usingDemoCollaborators ? handleRemoveCollaborator : undefined}
              messages={usingDemoCollaborators ? demoMessages : undefined}
              onSendMessage={usingDemoCollaborators ? handleSendCollabMessage : undefined}
              documentTitle={activeDocument?.title}
              currentUserName={currentUser.name}
            />
          );
        default: {
          const tab = rightPanelTabs.find((t) => t.id === activeRightTab);
          return (
            <div className="h-full w-full bg-white/5 rounded-b-xl px-4 py-3 overflow-auto text-sm text-slate-200">
              <h3 className="font-semibold text-white mb-1">
                {tab ? tab.label : "Panel"}
              </h3>
              <p className="text-xs text-slate-300">Coming soon...</p>
            </div>
          );
        }
      }
    })();

    return (
      <div className="h-full flex flex-col gap-4">
        <div className="bg-slate-900/40 border border-white/10 rounded-2xl shadow-inner flex flex-col min-h-[280px] max-h-[460px]">
          {assistantError && (
            <div className="px-4 py-2 text-xs text-amber-200 bg-amber-500/10 border-b border-amber-500/30">
              {assistantError}
            </div>
          )}
          <div className="flex-1 min-h-0">
            <AIAssistPanel
              messages={assistantMessages}
              busy={assistantBusy}
              onSendChat={sendAssistantChat}
              onRunCommand={runAssistantCommand}
              onQuickCommand={handleAssistantQuickCommand}
            />
          </div>
        </div>
        <div className="flex-1 min-h-0">{panelContent}</div>
      </div>
    );
  }

  /* ---------- Render ---------- */
  // derive page colors from editorMode context
  const pageBg = editorMode === "white" ? "#ffffff" : "#18181b";
  const pageText = editorMode === "white" ? "#222222" : "#f8fafc";
  const isLightTheme = theme === "maroon";
  const saveDisabled = saving || (!hasChanges && Boolean(activeDocumentId));
  const headerAccentColor = editorMode === "white" ? "#111827" : "rgba(226,232,240,0.9)";
  const headerBandBackground = editorMode === "white" ? "rgba(248,250,252,0.85)" : "rgba(15,23,42,0.9)";

  const organizerTeaserClass = [
    "hidden lg:flex flex-col rounded-[28px] border backdrop-blur-2xl relative overflow-hidden",
    theme === "purple"
      ? "border-purple-400/40 bg-gradient-to-b from-[#2c0f4d]/90 via-[#1a082c]/90 to-[#05000e]/90 shadow-[0_30px_90px_rgba(137,95,255,0.35)]"
      : isLightTheme
      ? "border-slate-200 bg-white shadow-[0_25px_70px_rgba(15,23,42,0.1)]"
      : "border-slate-800 bg-slate-950/80 shadow-[0_25px_70px_rgba(5,5,15,0.65)]",
  ].join(" ");

  const organizerBadgeClass =
    "inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-semibold tracking-[0.26em] uppercase " +
    (theme === "purple"
      ? "bg-white/10 text-amber-200"
      : isLightTheme
      ? "bg-purple-100 text-purple-700"
      : "bg-slate-800 text-amber-200");

  const organizerPrimaryTextClass =
    theme === "purple" ? "text-white" : isLightTheme ? "text-slate-900" : "text-white";

  const organizerSecondaryTextClass =
    theme === "purple"
      ? "text-white/70"
      : isLightTheme
      ? "text-slate-500"
      : "text-slate-300";

  const organizerGlowTextClass =
    theme === "purple"
      ? "text-white/85"
      : isLightTheme
      ? "text-slate-600"
      : "text-slate-200";

  const organizerCardShellClass =
    theme === "purple"
      ? "border-white/15 bg-white/5"
      : isLightTheme
      ? "border-slate-200 bg-slate-50"
      : "border-white/15 bg-white/5";

  const organizerChipClass = (tone: OrganizerTone) => {
    const base = "px-3 py-1.5 rounded-full text-[11px] font-medium border";
    switch (tone) {
      case "experiential":
        return `${base} bg-emerald-500/15 border-emerald-400/40 text-emerald-200`;
      case "meeting":
        return `${base} bg-cyan-500/15 border-cyan-400/40 text-cyan-200`;
      case "assignment":
      default:
        return `${base} bg-fuchsia-500/15 border-fuchsia-400/40 text-fuchsia-100`;
    }
  };

  const headerPrimaryTextClass = isLightTheme ? "text-rose-950" : "text-white";
  const headerSecondaryTextClass = isLightTheme ? "text-rose-600" : "text-white/60";
  const topicChipClass = isLightTheme
    ? "px-3 py-1 rounded-full border border-rose-200 text-rose-900 bg-white/70"
    : "px-3 py-1 rounded-full border border-white/20 text-white/80";
  const logoBorderClass = isLightTheme ? "border-rose-200" : "border-white/20";

  const handleOpenOrganizer = () => {
    const params = new URLSearchParams();
    params.set("from", "editor");
    if (documentIdForPanels) params.set("documentId", String(documentIdForPanels));
    if (title) params.set("title", title);
    if (paperTopic) params.set("topic", paperTopic);
    navigate(`/organizer?${params.toString()}`);
  };
  return (
    <Layout>
      <div className="flex flex-col gap-4 px-4 md:px-8 py-4">
        <header className={`flex flex-wrap items-start gap-4 ${headerPrimaryTextClass}`}>
          <div className="flex flex-col gap-3 flex-1 min-w-[240px]">
            <div className="flex items-center gap-3">
              <img
                src="/BBQ-animated logo.gif"
                alt="ThesiScribe logo"
                className={`h-10 w-10 rounded-full object-cover border ${logoBorderClass}`}
              />
              <div className="leading-tight">
                <p className={`text-sm font-semibold ${headerPrimaryTextClass}`}>ThesiScribe</p>
                <p className={`text-[11px] tracking-[0.35em] uppercase ${headerSecondaryTextClass}`}>
                  Scholarly Writing Assistant
                </p>
              </div>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-[0.35em] ${headerSecondaryTextClass}`}>
                Active Document
              </p>
              <h1 className={`text-2xl font-semibold ${headerPrimaryTextClass}`}>
                {title || "Untitled Document"}
              </h1>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs">
            <span className={`uppercase tracking-[0.35em] ${headerSecondaryTextClass}`}>Topic</span>
            <span className={topicChipClass}>
              {paperTopic || "Set a topic to unlock insights"}
            </span>
          </div>
        </header>

        <section className="glass-panel border border-white/10 rounded-2xl p-4 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setHasChanges(true);
                }}
                className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 min-w-[220px]"
                placeholder="Document title"
              />
              <select
                value={formatStyle}
                onChange={handleFormatChange}
                className="h-9 px-3 rounded-lg bg-purple-500/40 border border-purple-400/70 text-xs text-purple-100 focus:outline-none"
              >
                <option value="APA">APA Style</option>
                <option value="MLA">MLA Style</option>
                <option value="Chicago">Chicago</option>
                <option value="Turabian">Turabian</option>
                <option value="Harvard">Harvard</option>
                <option value="AMA">AMA</option>
                <option value="IEEE">IEEE</option>
              </select>
              <button
                onClick={handleOpenStyleGuide}
                className="h-9 px-3 rounded-lg text-[11px] bg-white/5 border border-white/10 text-slate-100 hover:bg-white/10"
              >
                Open {formatStyle} guide
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <div
                className="flex items-center gap-2 text-xs bg-white/5 rounded-full px-3 py-1.5 border border-white/15"
                title="Live word count and AI originality check"
              >
                <span className="font-semibold text-white">📝 {wordCount} words</span>
                <span className="text-white/30">|</span>
                <span className="font-semibold text-emerald-400">🤖 {aiUsage}% AI</span>
              </div>
              {notebookContext && (
                <span className="text-[11px] uppercase tracking-[0.35em] bg-emerald-500/10 border border-emerald-400/40 rounded-full px-3 py-1 text-emerald-200 whitespace-nowrap">
                  Linked to {notebookContext.notebookTitle} • {notebookContext.tabLabel}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              onClick={handleToggleDictation}
              className={`h-9 px-3 rounded-full border text-xs font-semibold flex items-center gap-2 ${
                dictationSupported
                  ? isDictating
                    ? "border-rose-300 text-rose-100 bg-rose-500/10"
                    : "border-white/20 text-white hover:bg-white/10"
                  : "border-white/10 text-slate-400 cursor-not-allowed"
              }`}
            >
              {isDictating ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
              {dictationSupported ? (isDictating ? "Stop" : "Dictate") : "Dictation"}
            </button>
            {dictationStatus && (
              <span className="text-[11px] text-slate-300 min-w-[120px]">
                {dictationStatus}
              </span>
            )}
            <button
              type="button"
              onClick={handleShowMusicPlayer}
              className="h-9 px-3 rounded-full border border-white/20 text-xs text-white flex items-center gap-2 bg-gradient-to-r from-rose-500/20 via-orange-500/20 to-amber-400/20"
            >
              <Headphones className="w-3.5 h-3.5" />
              Music
            </button>
            <button
              type="button"
              onClick={() => setSnackPanelOpen(true)}
              className="h-9 px-3 rounded-full border border-white/20 text-xs text-white flex items-center gap-2 bg-gradient-to-r from-amber-500/25 via-rose-500/25 to-red-500/25"
            >
              <Utensils className="w-3.5 h-3.5" />
              Snacks
            </button>
            <div className="flex items-center gap-1 rounded-full border border-white/15 bg-gradient-to-r from-purple-600/30 via-pink-500/30 to-blue-500/30 backdrop-blur px-2 py-1">
              <button
                type="button"
                onClick={handleNewDocument}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white border border-white/25 hover:bg-white/20 transition"
              >
                <FilePlus2 className="w-3.5 h-3.5" />
                <span>New</span>
              </button>
              <button
                type="button"
                onClick={() => setDocumentPickerOpen(true)}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white border border-white/25 hover:bg-white/20 transition"
              >
                <FolderOpen className="w-3.5 h-3.5" />
                <span>Open</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveRightTab("upload")}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white border border-white/25 hover:bg-white/20 transition"
              >
                <UploadCloud className="w-3.5 h-3.5" />
                <span>Upload</span>
              </button>
              <button
                type="button"
                onClick={handleSaveDocument}
                disabled={saveDisabled}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white border border-white/25 hover:bg-white/20 transition ${
                  saveDisabled ? "opacity-40 cursor-not-allowed" : ""
                }`}
              >
                <Save className="w-3.5 h-3.5" />
                <span>{saving ? "Saving" : "Save"}</span>
              </button>
              <button
                type="button"
                onClick={handlePrintDocument}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white border border-white/25 hover:bg-white/20 transition"
              >
                <Printer className="w-3.5 h-3.5" />
                <span>Print</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    disabled={Boolean(exportingFormat)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white border border-white/25 transition ${
                      exportingFormat ? "opacity-40 cursor-not-allowed" : "hover:bg-white/20"
                    }`}
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    <span>
                      {exportingFormat ? `Exporting ${exportingFormat.toUpperCase()}` : "Export"}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border-white/20 text-white">
                  <DropdownMenuItem
                    disabled={Boolean(exportingFormat)}
                    onSelect={(event) => {
                      event.preventDefault();
                      if (!exportingFormat) handleExportDocument("pdf");
                    }}
                  >
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={Boolean(exportingFormat)}
                    onSelect={(event) => {
                      event.preventDefault();
                      if (!exportingFormat) handleExportDocument("docx");
                    }}
                  >
                    Export as DOCX
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </section>

        {/* MAIN AREA */}
      <div className="flex-1 flex min-h-[calc(100vh-116px)] px-4 md:px-6 pb-4 gap-4">
        {/* EDITOR */}
        <div className="flex-1 flex flex-col glass-panel overflow-hidden rounded-xl min-h-0">
          {/* Editor toolbar */}
          <div
            id="editor-toolbar"
            className="flex flex-wrap gap-1 items-center bg-white/5 p-2 border-b border-white/10"
          >
            <select className="ql-header h-8 text-xs bg-white/5 border border-white/10 rounded px-2">
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="">Normal</option>
            </select>
            <button className="ql-bold h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Bold" />
            <button className="ql-italic h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Italic" />
            <button className="ql-underline h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Underline" />
            <button className="ql-strike h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Strikethrough" />
            <button className="ql-blockquote h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Blockquote" />
            <div className="w-px h-5 bg-white/10" />
            <button className="ql-list h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" value="ordered" title="Ordered list" />
            <button className="ql-list h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" value="bullet" title="Bullet list" />
            <div className="w-px h-5 bg-white/10" />
            <button className="ql-align h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" value="" title="Align left" />
            <button className="ql-align h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" value="center" title="Align center" />
            <button className="ql-align h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" value="right" title="Align right" />
            <button className="ql-align h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" value="justify" title="Justify" />
            <div className="w-px h-5 bg-white/10" />
            <button className="ql-link h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Link" />
            <button className="ql-clean h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Clear format" />
            <button className="ql-code-block h-8 w-8 rounded bg-white/5 border border-white/10 hover:bg-white/10" title="Code Block" />
            <div className="w-px h-5 bg-white/10" />
            <button className="ql-pageBreak h-8 px-3 rounded bg-white/5 border border-white/10 hover:bg-white/10 text-[10px] font-semibold uppercase tracking-[0.2em]" title="Insert page break">
              Break
            </button>
          </div>

            <div className="bg-white/0 border-b border-white/10 px-4 py-3 flex flex-col gap-3">
              <div className="grid gap-3 md:grid-cols-4">
                <label className="flex flex-col gap-1 text-xs text-slate-300">
                  <span className="uppercase tracking-[0.35em] text-white/60">Header</span>
                  <input
                    value={headerText}
                    onChange={(e) => setHeaderText(e.target.value)}
                    placeholder="Add header text"
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-300">
                  <span className="uppercase tracking-[0.35em] text-white/60">Footer</span>
                  <input
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    placeholder="Add footer text"
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                  />
                </label>
                <div className="flex flex-col gap-2 text-xs text-slate-200">
                  <label className="uppercase tracking-[0.35em] text-white/60">Page layout</label>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select
                      value={marginPreset}
                      onChange={(e) => setMarginPreset(e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                    >
                      {Object.entries(marginPresets).map(([key, config]) => (
                        <option key={key} value={key}>
                          {config.label}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
                      <span>Page #</span>
                      <Switch checked={showPageNumbers} onCheckedChange={setShowPageNumbers} />
                    </div>
                    <button
                      type="button"
                      onClick={handleInsertPageBreak}
                      className="text-xs font-semibold px-3 py-2 border border-white/15 rounded-full hover:bg-white/10 transition"
                    >
                      Insert Page Break
                    </button>
                  </div>
                </div>
                <label className="flex flex-col gap-1 text-xs text-slate-300">
                  <span className="uppercase tracking-[0.35em] text-white/60">Paper Topic</span>
                  <input
                    value={paperTopic}
                    onChange={(e) => setPaperTopic(e.target.value)}
                    placeholder="e.g., Urban belonging and resilience"
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                  />
                </label>
              </div>
              <div className="flex items-center gap-3 flex-wrap text-xs text-white/70">
                <span className="uppercase tracking-[0.35em] text-white/50">Pages</span>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: estimatedPageCount }).map((_, idx) => (
                    <button
                      key={`page-chip-${idx}`}
                      type="button"
                      onClick={() => scrollToPage(idx)}
                      className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${
                        visiblePage === idx + 1
                          ? "border-white text-slate-900 bg-white"
                          : "border-white/30 text-white/80 hover:border-white/60"
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
                <span className="text-[11px] tracking-[0.2em] text-white/60">
                  Page {visiblePage} / {estimatedPageCount}
                </span>
              </div>
              <div className="w-full space-y-3">
                <div className="flex items-center justify-between text-xs text-white/70">
                  <span className="uppercase tracking-[0.35em] text-white/60">
                    Title Page Formatter
                  </span>
                  <span className="text-[10px] text-white/50">Style: {formatStyle}</span>
                </div>
                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(Object.keys(TITLE_FIELD_LABELS) as Array<keyof TitlePageFields>).map((key) => (
                      <label key={key} className="flex flex-col gap-1 text-xs text-white/70">
                        <span className="uppercase tracking-[0.3em] text-white/50">
                          {TITLE_FIELD_LABELS[key]}
                        </span>
                        <input
                          value={titlePageFields[key]}
                          onChange={(e) => handleTitlePageFieldChange(key, e.target.value)}
                          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                    <p className="text-xs uppercase tracking-[0.35em] text-white/60">
                      Preview
                    </p>
                    <div className="flex-1 bg-white/5 rounded-xl px-4 py-6 text-center text-sm text-white/80 font-serif border border-white/10 min-h-[160px] whitespace-pre-line">
                      {titlePagePreviewLines.map((line, idx) => (
                        <p key={`title-line-${idx}`} className="leading-relaxed">
                          {line || "\u00A0"}
                        </p>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleInsertFormattedTitlePage}
                      className="px-4 py-2 rounded-full border border-white/20 text-white text-xs font-semibold hover:bg-white/10 transition"
                    >
                      Insert Title Page
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Page‑like editor area */}
          <div
            ref={editorScrollRef}
            className={
              "flex-1 min-h-0 flex items-start justify-center py-6 overflow-y-auto " +
              (theme === "purple"
                ? "bg-purple-950"
                : theme === "maroon"
                ? "bg-rose-50"
                : "bg-black")
            }
          >
            <div
              ref={printableAreaRef}
              className="relative printable-page"
              style={{
                width: "8.5in",
                minHeight: "11in",
                backgroundColor: pageBg,
                border: "1px solid #ffffff",
                boxShadow: "0 0 40px rgba(0,0,0,0.8)",
              }}
            >
              <style>{`
                .ql-container {
                  border: none !important;
                  height: 100% !important;
                  font-family: "Times New Roman", serif !important;
                  font-size: 12pt !important;
                  background: ${pageBg} !important;
                }
                .ql-editor {
                  padding: ${editorPaddingTop}px ${activeMargin.value}px ${editorPaddingBottom}px ${activeMargin.value}px !important;
                  line-height: 1.8;
                  color: ${pageText} !important;
                  min-height: 100%;
                  box-sizing: border-box;
                  background: ${pageBg} !important;
                }
                .ql-toolbar.ql-snow button,
                .ql-toolbar.ql-snow .ql-picker-label,
                .ql-toolbar.ql-snow .ql-picker-item {
                  color: rgba(248, 250, 252, 0.95) !important;
                }
                .ql-toolbar.ql-snow .ql-stroke {
                  stroke: rgba(248, 250, 252, 0.95) !important;
                }
                .ql-toolbar.ql-snow .ql-fill {
                  fill: rgba(248, 250, 252, 0.95) !important;
                }
                .printable-page {
                  counter-reset: page 1;
                }
                .ql-page-break {
                  border-top: 2px dashed rgba(226, 232, 240, 0.5);
                  margin: 2rem 0;
                  text-align: center;
                  position: relative;
                  counter-increment: page;
                }
                .ql-page-break-label {
                  font-size: 10px;
                  letter-spacing: 0.35em;
                  text-transform: uppercase;
                  color: rgba(255,255,255,0.6);
                  background: rgba(15,23,42,0.9);
                  padding: 0.15rem 0.75rem;
                  border-radius: 999px;
                  position: absolute;
                  top: -0.75rem;
                  left: 50%;
                  transform: translateX(-50%);
                  border: 1px solid rgba(255,255,255,0.2);
                }
                .ql-page-break::after {
                  content: 'Page ' counter(page) ' begins here';
                  display: block;
                  margin-top: 0.75rem;
                  font-size: 11px;
                  letter-spacing: 0.3em;
                  color: rgba(226, 232, 240, 0.7);
                }
                .page-header-preview,
                .page-header-preview,
                .page-footer-preview {
                  position: absolute;
                  left: ${activeMargin.value}px;
                  width: calc(100% - ${activeMargin.value * 2}px);
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  font-size: 12px;
                  letter-spacing: 0.08em;
                  text-transform: uppercase;
                  color: ${headerAccentColor};
                  background: ${headerBandBackground};
                  padding: 4px 18px;
                  border-radius: 999px;
                  border: 1px solid rgba(148, 163, 184, 0.25);
                  pointer-events: none;
                  box-shadow: inset 0 0 8px rgba(15, 23, 42, 0.12);
                }
                .page-header-preview {
                  top: ${Math.max(12, activeMargin.value * 0.35)}px;
                }
                .page-footer-preview {
                  bottom: ${Math.max(12, activeMargin.value * 0.35)}px;
                }
                }
                @media print {
                  body {
                    background: #ffffff !important;
                  }
                  .ql-editor {
                    color: #000000 !important;
                    background: #ffffff !important;
                    padding-top: ${activeMargin.value}px !important;
                    padding-bottom: ${activeMargin.value}px !important;
                  }
                  .ql-page-break {
                    border-color: transparent;
                    page-break-after: always;
                  }
                  .page-header-preview,
                  .page-footer-preview {
                    color: #000;
                  }
                  body.print-mode * {
                    visibility: hidden;
                  }
                  body.print-mode .printable-page,
                  body.print-mode .printable-page * {
                    visibility: visible;
                  }
                  body.print-mode .printable-page {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 8.5in;
                    margin: 0;
                  }
                  @page {
                    margin: 0;
                  }
                }
              `}</style>
              <div className="page-header-preview">
                <span>{headerText || ""}</span>
                {showPageNumbers && (
                  <span>Page 1{estimatedPageCount > 1 ? ` of ${estimatedPageCount}` : ""}</span>
                )}
              </div>
              <ReactQuill
                ref={quillRef}
                value={content}
                onChange={(value) => {
                  setContent(value);
                  setHasChanges(true);
                }}
                modules={modules}
                formats={formats}
                theme="snow"
                className="h-full"
              />
              <div className="page-footer-preview">
                <span>{footerText || ""}</span>
                {showPageNumbers && (
                  <span>
                    Page 1{estimatedPageCount > 1 ? ` of ${estimatedPageCount}` : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ORGANIZER TEASER */}
        <aside className={organizerTeaserClass} style={{ width: 260 }}>
          <div className="absolute inset-0 opacity-60 pointer-events-none">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15), transparent 35%)," +
                  "radial-gradient(circle at 80% 0%, rgba(255,255,255,0.12), transparent 40%)",
              }}
            />
          </div>
          <div className="p-4 relative space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <span className={organizerBadgeClass}>
                  <Sparkles className="w-3 h-3" /> ORG
                </span>
                <p className={`text-sm font-semibold tracking-wide ${organizerPrimaryTextClass}`}>
                  ThesiScribe Organizer
                </p>
                <p className={`text-xs ${organizerSecondaryTextClass}`}>
                  Connected to {organizerSummary.course} • {organizerSummary.instructor}
                </p>
              </div>
              <div className="flex flex-col items-end text-right">
                <span className="text-[10px] uppercase tracking-[0.25em] text-amber-200">
                  Upcoming
                </span>
                <span className={`text-3xl font-black ${organizerPrimaryTextClass} leading-none`}>
                  {organizerSummary.upcomingCount}
                </span>
              </div>
            </div>

            <div className={`rounded-2xl border px-4 py-3 space-y-1 ${organizerCardShellClass}`}>
              <div className={`flex items-center justify-between text-xs ${organizerSecondaryTextClass}`}>
                <span className={`inline-flex items-center gap-1 font-semibold ${organizerPrimaryTextClass}`}>
                  <Bell className="w-3.5 h-3.5 text-amber-200" /> Next Due
                </span>
                <span>{organizerSummary.dueDisplay}</span>
              </div>
              <p className={`text-base font-semibold ${organizerPrimaryTextClass}`}>
                {organizerSummary.nextItemLabel}
              </p>
              <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-300">
                AI-Ready • One-Click Draft
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {organizerTaskChips.map((chip) => (
                <span key={chip.id} className={organizerChipClass(chip.tone)}>
                  {chip.label}
                </span>
              ))}
            </div>

            <div className={`rounded-2xl border p-4 space-y-3 ${organizerCardShellClass}`}>
              <p className={`text-xs ${organizerGlowTextClass} leading-relaxed`}>
                Auto-sync discussions, experiential logs, and live meeting recaps so every draft starts with context.
              </p>
              <button
                className="w-full flex items-center justify-center gap-2 rounded-2xl bg-white text-slate-900 text-xs font-semibold py-2 shadow-[0_12px_25px_rgba(15,15,35,0.25)]"
                type="button"
                onClick={handleOpenOrganizer}
              >
                Open Organizer <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-200 text-center">
                Linked to Writing Assistant
              </div>
            </div>
          </div>
        </aside>

        {/* RIGHT PANEL */}
        <div
          className="hidden md:flex flex-col bg-white/5 border border-white/10 rounded-xl overflow-hidden relative min-h-0"
          style={{ width: sidebarWidth }}
        >
          <div className="grid grid-cols-4 gap-1 bg-white/10 border-b border-white/10 p-2">
            <div className="col-span-4 text-xs font-semibold text-purple-300 mb-1">
              Reference Management
            </div>
            {citationTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveRightTab(tab.id)}
                className={getTabButtonClass(tab.id, activeRightTab === tab.id)}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}

            <div className="col-span-4 text-xs font-semibold text-slate-400 mt-2 mb-1">
              Writing Tools
            </div>
            {otherTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveRightTab(tab.id)}
                className={getTabButtonClass(tab.id, activeRightTab === tab.id)}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 min-h-0">{renderRightPanelContent()}</div>
        </div>
      </div>
      </div>

      <Dialog open={snackPanelOpen} onOpenChange={setSnackPanelOpen}>
        <DialogContent className="max-w-4xl w-full bg-slate-950 border border-white/10 text-white">
          <LateNightSnackPanel
            spots={snackSpots}
            loading={snackLoading}
            onCreate={handleCreateSnackSpot}
            onUpdate={handleUpdateSnackSpot}
            onDelete={handleDeleteSnackSpot}
            onMarkOrdered={handleMarkSnackOrdered}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={documentPickerOpen} onOpenChange={setDocumentPickerOpen}>
        <DialogContent className="bg-slate-950 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Select a document</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose a draft to load into the editor or start a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {availableDocuments.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => handleSelectDocument(doc)}
                className="w-full text-left rounded-lg border border-white/10 px-3 py-2 hover:bg-white/10 transition"
              >
                <p className="text-sm font-semibold">{doc.title}</p>
                <p className="text-[11px] text-slate-400">
                  Last updated {new Date(doc.updated_at).toLocaleString()}
                </p>
              </button>
            ))}
            {availableDocuments.length === 0 && (
              <p className="text-sm text-slate-400">No documents yet.</p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDocumentPickerOpen(false)}
              className="px-4 py-2 rounded-lg border border-white/20 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                handleNewDocument();
                setDocumentPickerOpen(false);
              }}
              className="px-4 py-2 rounded-lg bg-purple-600 text-sm font-semibold"
            >
              New Document
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EditorPage;